import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConvectionOvenAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDishwasherAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDoubleOvenAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFreezerAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGarbageDisposalAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMicrowaveOvenAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOvenAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRangeAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRefrigeratorAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrashCompactorAppliance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWasherDryerHookupsAppliance,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { AMENITY_SAVE_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { amenityPatches } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import { APPLIANCE_KEYS, APPLIANCE_METADATA, type ApplianceKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

import { type AmenityFlagSave } from '../../utils';

type LocalState = Record<ApplianceKey, boolean>;

const AppliancesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const applianceTypes = listingDetails?.buildingAttributes?.applianceTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasConvectionOven: applianceTypes?.hasConvectionOven ?? false,
    hasDishwasher: applianceTypes?.hasDishwasher ?? false,
    hasDoubleOven: applianceTypes?.hasDoubleOven ?? false,
    hasFreezer: applianceTypes?.hasFreezer ?? false,
    hasGarbageDisposal: applianceTypes?.hasGarbageDisposal ?? false,
    hasWasherDryerHookups: applianceTypes?.hasWasherDryerHookups ?? false,
    hasMicrowaveOven: applianceTypes?.hasMicrowaveOven ?? false,
    hasOven: applianceTypes?.hasOven ?? false,
    hasRange: applianceTypes?.hasRange ?? false,
    hasRefrigerator: applianceTypes?.hasRefrigerator ?? false,
    hasTrashCompactor: applianceTypes?.hasTrashCompactor ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Appliances updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateConvectionOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConvectionOvenAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConvectionOvenAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateDishwasher } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDishwasherAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDishwasherAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateDoubleOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDoubleOvenAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDoubleOvenAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateFreezer } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFreezerAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFreezerAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateGarbageDisposal } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGarbageDisposalAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGarbageDisposalAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateWasherDryerHookups } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWasherDryerHookupsAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWasherDryerHookupsAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMicrowaveOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMicrowaveOvenAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMicrowaveOvenAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOvenAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOvenAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateRange } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRangeAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRangeAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateRefrigerator } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRefrigeratorAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRefrigeratorAppliance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateTrashCompactor } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrashCompactorAppliance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrashCompactorAppliance',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasConvectionOven: updateConvectionOven,
    hasDishwasher: updateDishwasher,
    hasDoubleOven: updateDoubleOven,
    hasFreezer: updateFreezer,
    hasGarbageDisposal: updateGarbageDisposal,
    hasWasherDryerHookups: updateWasherDryerHookups,
    hasMicrowaveOven: updateMicrowaveOven,
    hasOven: updateOven,
    hasRange: updateRange,
    hasRefrigerator: updateRefrigerator,
    hasTrashCompactor: updateTrashCompactor,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ApplianceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = APPLIANCE_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.feedFieldName ?? metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, AMENITY_SAVE_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Appliances</Legend>} disabled={isInputFormDisabled}>
      {APPLIANCE_KEYS.map((fieldName) => {
        const metadata = APPLIANCE_METADATA[fieldName];
        return (
          <LabeledControl
            key={`applianceTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(AppliancesAmenities);
